import router from '../../router'
import Store from '../../store'
import { api, i18n } from '@/plugins'
import Vue, { getCurrentInstance, onMounted, onBeforeUnmount } from 'vue'
import {
  endpoints,
  showError,
  query,
  convertToWidthCharacter,
  romajiToKana
} from '@/utils'
import { TaxType, TaxTypeId } from '@/utils/constants'
import moment from 'moment'
import _ from 'lodash'
import { DAILY_UPDATE } from '../apiEndpoints'
import { TaxFractionId } from '../constants'
import { getCodeNameStr } from '../string'

export const replaceUrl = ({ query, params }) => {
  // type query and params is object
  // exp: {id: 1}
  return router
    .replace({
      query: query,
      params: params
    })
    .catch((e) => {
      return
    })
}

// 検索条件を現在のURLクエリへマージして反映する。空値のキーは残さない。
export const replaceUrlQuery = (patch) => {
  const query = { ...router.currentRoute.query, ...patch }
  Object.keys(query).forEach((key) => {
    const value = query[key]
    if (value === null || value === undefined || value === '') delete query[key]
  })
  return replaceUrl({ query })
}

// URLクエリの 'true' / 'false' を真偽値に戻す。未指定は null。
export const parseQueryBoolean = (value) => {
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

export const numberToString = (
  number,
  isRounding = false,
  maximumFractionDigits = 2,
  showEmpty = false
) => {
  if (number && isRounding) number = Math.round(number)
  if ((number === null || number === undefined) && showEmpty) {
    return ''
  }
  return number
    ? number.toLocaleString('en-US', { maximumFractionDigits })
    : '0'
}

export const numberOrEmpty = (number, maximumFractionDigits = 2) =>
  numberToString(number, false, maximumFractionDigits, true)

export const convertMinusJpToLatin = (input) => {
  // convert string '-' fullwidth, halfwidth to '-' latin
  let result = input
    ? input
        .toString()
        .replaceAll(/−|ｰ|ー/gi, '-')
        .normalize('NFKD')
    : ''
  if (isNaN(result)) {
    result = ''
  }
  return result
}

export const backPage = () => {
  history.back()
}

export const formatArrivalNo = (
  arrival_no,
  arrival_detail_no,
  splitter = '-'
) => {
  if (arrival_no == null || arrival_detail_no == null) return ''
  return `${String(arrival_no).padStart(5, '0')}${splitter}${String(
    arrival_detail_no
  )}`
}

export const getData = async (listData = [], queryData = {}, getOne = {}) => {
  const result = {
    items: null,
    varieties: null,
    colors: null,
    units: null,
    grades: null,
    arrival_types: null,
    articles: null,
    daily_update: null
  }

  const queryItem = queryData.item ? query.buildQuery(queryData.item) : ''
  const queryVariety = queryData.variety
    ? query.buildQuery(queryData.variety)
    : ''
  const queryGrade = queryData.grade ? query.buildQuery(queryData.grade) : ''
  const queryRank = queryData.rank ? query.buildQuery(queryData.rank) : ''
  const queryPacking = queryData.packing
    ? query.buildQuery(queryData.packing)
    : 'active=true'
  const queryPast = queryData.past ? query.buildQuery(queryData.past) : ''
  const queryShipper = queryData.shipper
    ? query.buildQuery(queryData.shipper)
    : 'page=1&per_page=100&active=true'
  const queryBuyer = queryData.buyer
    ? query.buildQuery(queryData.buyer)
    : 'page=1&per_page=100&active=true'
  const varietyId = getOne.variety || ''
  try {
    await Promise.all([
      listData.includes('item')
        ? api
            .get(`${endpoints.MASTER_ITEM}?${queryItem}`)
            .then((value) => (result.items = value.data))
        : null,
      listData.includes('past_item')
        ? api
            .get(`${endpoints.PAST_ITEM}?${queryItem}&${queryPast}`)
            .then((value) => (result.items = value.data))
        : null,
      listData.includes('variety')
        ? api
            .get(`${endpoints.MASTER_VARIETY}${varietyId}?${queryVariety}`)
            .then((value) => {
              result.varieties = value.data
              result.total = {
                varieties: Number(value.headers['x-item-count']) || 0
              }
            })
        : null,
      listData.includes('past_variety')
        ? api
            .get(
              `${endpoints.PAST_VARIETY}${varietyId}?${queryVariety}&${queryPast}`
            )
            .then((value) => (result.varieties = value.data))
        : null,
      listData.includes('color')
        ? api
            .get(endpoints.MASTER_COLOR)
            .then((value) => (result.colors = value.data))
        : null,
      listData.includes('arrival_type')
        ? api
            .get(endpoints.MASTER_ARRIVAL_TYPE)
            .then((value) => (result.arrival_types = value.data))
        : null,
      listData.includes('grade')
        ? api
            .get(`${endpoints.MASTER_GRADE}?${queryGrade}`)
            .then((value) => (result.grades = value.data))
        : null,
      listData.includes('rank')
        ? api
            .get(`${endpoints.MASTER_RANK}?${queryRank}`)
            .then((value) => (result.ranks = value.data))
        : null,
      listData.includes('past_grade')
        ? api.get(`${endpoints.PAST_GRADE}?${queryPast}`).then((value) => {
            if (listData.includes('past_grade')) result.grades = value.data
          })
        : null,
      listData.includes('past_rank')
        ? api.get(`${endpoints.PAST_RANK}?${queryPast}`).then((value) => {
            if (listData.includes('past_rank')) result.ranks = value.data
          })
        : null,
      listData.includes('bloom_num')
        ? api
            .get(endpoints.MASTER_BLOOM)
            .then((value) => (result.bloom_nums = value.data))
        : null,
      listData.includes('past_bloom_num')
        ? api
            .get(`${endpoints.PAST_BLOOM_NUM}?${queryPast}`)
            .then((value) => (result.bloom_nums = value.data))
        : null,
      listData.includes('packing')
        ? api
            .get(
              `${endpoints.MASTER_PACKING}${
                queryPacking ? '?' + queryPacking : ''
              }`
            )
            .then((value) => (result.packings = value.data))
        : null,
      listData.includes('article')
        ? api
            .get(endpoints.MASTER_ARTICLE)
            .then((value) => (result.articles = value.data))
        : null,
      listData.includes('past_article')
        ? api
            .get(`${endpoints.PAST_ARTICLE}?${queryPast}`)
            .then((value) => (result.articles = value.data))
        : null,
      listData.includes('country')
        ? api
            .get(endpoints.MASTER_COUNTRY)
            .then((value) => (result.countries = value.data))
        : null,
      listData.includes('mps')
        ? api
            .get(endpoints.MASTER_MPS)
            .then((value) => (result.mps = value.data))
        : null,
      listData.includes('daily_update')
        ? api
            .get(endpoints.DAILY_UPDATE)
            .then((value) => (result.daily_update = value.data))
        : null,
      listData.includes('unit')
        ? api
            .get(`${endpoints.MASTER_UNIT}?active=true`)
            .then((value) => (result.units = value.data))
        : null,
      listData.includes('prefecture')
        ? api
            .get(endpoints.MASTER_PREFECTURE)
            .then((value) => (result.prefectures = value.data))
        : null,
      listData.includes('organization_type')
        ? api
            .get(endpoints.MASTER_ORGANIZATION_TYPE)
            .then((value) => (result.organizationTypes = value.data))
        : null,
      listData.includes('shipper')
        ? api
            .get(`${endpoints.MASTER_SHIPPER}?${queryShipper}`)
            .then((value) => {
              result.shippers = value.data
              result.total = {
                shippers: Number(value.headers['x-item-count']) || 0
              }
            })
        : null,
      listData.includes('buyer')
        ? api
            .get(`${endpoints.MASTER_BUYER}?${queryBuyer}`)
            .then((value) => (result.buyers = value.data))
        : null,
      listData.includes('carrier')
        ? api
            .get(endpoints.MASTER_CARRIER)
            .then((value) => (result.carriers = value.data))
        : null,
      listData.includes('tax_type')
        ? api
            .get(endpoints.MASTER_TAX_TYPE)
            .then((value) => (result.tax_types = value.data))
        : null,
      listData.includes('agri_news_categories')
        ? api
            .get(endpoints.MASTER_AGRI_NEWS_CATEGORIES)
            .then((value) => (result.agri_news_categories = value.data))
        : null
    ])
  } catch (e) {
    showError(e, Vue.$toast, i18n.t('message.system_failure'))
  }
  return result
}
const returnDate = (year, month, date, format) => {
  let returnYear = year
  if (parseInt(year) < 100) {
    returnYear = `20${year}`
  } else if (year === null) {
    returnYear = new Date().getFullYear()
  }
  let lastDayOfMonth = moment(`${returnYear}-${month}`)
    .endOf('month')
    .format('DD')
  let dateOfMonth = date
  let monthOfYear = parseInt(month) - 1
  if (month > 12) {
    monthOfYear = 11
  }
  if (parseInt(date) > parseInt(lastDayOfMonth)) {
    dateOfMonth = lastDayOfMonth
  }
  if (lastDayOfMonth === 'Invalid date') {
    // if (year === null) {
    //     return `${month}${date}`
    // }
    // return `${year}${month}${date}`
    return null
  }
  return moment()
    .year(returnYear)
    .month(monthOfYear)
    .date(dateOfMonth)
    .format(format)
}
export const pickDate = (input, format = 'YYYY-MM-DD') => {
  input = convertToWidthCharacter(input, 'half')

  if (input === null) {
    return input
  }
  if (input === undefined) {
    return input
  }
  const v = input.trim()
  // 当月月末日を取得
  const last = moment().endOf('month').date()

  // 数値1-2桁のみ受け付ける
  switch (input.length) {
    case 1: {
      // 「a」なら月初
      if (v === 'a') {
        return moment().date(1).format(format)
      }
      // 「z」なら月末
      if (v === 'z') {
        return moment().date(last).format(format)
      }
      // 「0」なら当日
      if (v === '0') {
        return moment().format(format)
      }

      if (parseInt(v) > 0 && parseInt(v) < 10) {
        return moment().date(parseInt(v)).format(format)
      }
      if (isNaN(parseInt(v))) {
        return null
      }
      return v
    }
    case 2: {
      const p = /^([0-9]{1,2})$/
      if (!p.test(v)) {
        return null
      }
      if (v === '00') {
        return null
      }
      // 当月月初から月末日までの日付分の数値を列挙
      const dates = [...Array(last).keys()].map((i) => ++i)
      // 当月の有効な日付のみ受け付ける
      if (!dates.includes(parseInt(v, 10))) {
        return v
      }
      // 日付を設定して、有効な日付か最終チェック。
      const x = moment().date(v)
      if (x.isValid()) {
        return x.format(format)
      }
      return v
    }
    case 3: {
      let month3 = input.slice(0, 1)
      let day3 = input.slice(1, 3)
      return returnDate(null, '0' + month3, day3, format)
    }
    case 4: {
      let month4 = input.slice(0, 2)
      let day4 = input.slice(2, 4)
      return returnDate(null, month4, day4, format)
    }

    case 6: {
      let year6 = input.slice(0, 2)
      let month6 = input.slice(2, 4)
      let day6 = input.slice(4, 6)
      return returnDate(year6, month6, day6, format)
    }
    case 8: {
      let cleanedString = input.replace(/[/-]/g, '')
      let year
      let month
      let day
      if (cleanedString.length == 6) {
        year = cleanedString.slice(0, 2)
        month = cleanedString.slice(2, 4)
        day = cleanedString.slice(4, 6)
      } else {
        year = input.slice(0, 4)
        month = input.slice(4, 6)
        day = input.slice(6, 8)
      }
      return returnDate(year, month, day, format)
    }
    case 10: {
      let year10 = input.slice(0, 4)
      let month10 = input.slice(5, 7)
      let day10 = input.slice(8, 10)
      return returnDate(year10, month10, day10, format)
    }
    default:
      return null
  }
}

export const pickMonth = (input) => {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1 // Months are 0-indexed

  input = convertToWidthCharacter(input, 'half')
  if (input === null) {
    return input
  }
  if (input === undefined) {
    return input
  }

  if (input === '0') {
    return `${currentYear}-${currentMonth.toString().padStart(2, '0')}`
  }
  // Check if input is a single-digit or double-digit month
  if (Number(input) >= 1 && Number(input) <= 12) {
    const month = input.toString().padStart(2, '0') // Pad with leading zero if needed
    return `${currentYear}-${month}`
  }

  // Check if input is in the format [year last 2 numbers][month] or [year last 2 numbers]-[month]
  const yearLastTwoDigitsMonthRegex = /^\d{2}(\d{2}|-\d{2})$/
  if (yearLastTwoDigitsMonthRegex.test(input)) {
    const year = `20${input.substring(0, 2)}` // Assuming 21st century
    return `${year}-${input.substring(2).replace('-', '')}`
  }
  return null
}

export const pickYear = (input) => {
  const currentYear = new Date().getFullYear()

  input = convertToWidthCharacter(input, 'half')
  if (input === null || input === undefined) {
    return input
  }

  if (input === '0') {
    return currentYear.toString()
  }

  // 2 digits -> 20XX
  if (/^\d{2}$/.test(input)) {
    return `20${input}`
  }

  // 4 digits -> XXXX
  if (/^\d{4}$/.test(input)) {
    return input
  }

  return null
}

export const checkIsOnPC = () => {
  // BREAK USER AGENT DOWN
  const isMobile = navigator.userAgent.toLowerCase().match(/mobile/i)
  const isTablet = navigator.userAgent.toLowerCase().match(/tablet/i)
  const isAndroid = navigator.userAgent.toLowerCase().match(/android/i)
  const isiPhone = navigator.userAgent.toLowerCase().match(/iphone/i)
  const isiPad = navigator.userAgent.toLowerCase().match(/ipad/i)
  return !(isMobile || isTablet || isAndroid || isiPhone || isiPad)
}

export const checkScreenSize = () => {
  if (540 < screen.width && screen.width < 900) return 'tablet'
  else if (screen.width <= 540) return 'mobile'
  else return 'pc'
}

export const compactText = (text, maxLength) => {
  if (!text) return
  if (text.length < maxLength) {
    return text
  } else {
    return `${text.slice(0, maxLength - 1)}...`
  }
}

/**
 * Deep diff between two object, using lodash
 *
 * @param object
 * @param base
 * @param deep
 * @returns
 */
export function objectDiff(object, base, deep = false) {
  function changes(object, base) {
    return _.transform(object, function (result, value, key) {
      if (!_.isEqual(value, base[key])) {
        result[key] =
          _.isObject(value) && _.isObject(base[key]) && deep
            ? changes(value, base[key])
            : value === ''
            ? null
            : value
      }
    })
  }
  return changes(object, base)
}

export const calculateTax = (totalAmount, percentage, type) => {
  let product, tax
  if (type === TaxType.NO_TAX) {
    product = totalAmount
    tax = 0
  } else if (type === TaxType.INCLUDED) {
    product = (totalAmount / (percentage + 100)) * 100
    tax = totalAmount - product
  } else {
    product = totalAmount
    tax = (product * percentage) / 100
  }
  return [product, tax]
}

export const postData = async (endpoint, payload, showMsg = true) => {
  try {
    const { data } = await api.post(endpoint, payload)
    if (showMsg) Vue.$toast.success(i18n.t('common.msg.create_success'))
    return data
  } catch (e) {
    showError(e, Vue.$toast, i18n.t('common.msg.create_failed'))
  }
  return null
}

export const putData = async (endpoint, data) => {
  try {
    const response = await api.put(`${endpoint}`, data)
    Vue.$toast.success(i18n.t('common.msg.update_success'))
    return response.data
  } catch (e) {
    showError(e, Vue.$toast, i18n.t('common.msg.update_failed'))
  }
  return null
}

export const roundTax = (value, type) => {
  if (!value) return value
  if (type === 'ceil') return Math.ceil(value)
  else if (type === 'floor') return Math.floor(value)
  const round = Math.round(Math.abs(value))
  if (value < 0) return round * -1
  return round
}
export const validateEmail = (email) => {
  if (email) {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return emailRegex.test(email)
  } else return true
}
export const setupScroll = (
  wrapperScrollbarBottomRef,
  vTableRef,
  scrollBarBottomContainerRef
) => {
  const wrapperScrollbarBottom = wrapperScrollbarBottomRef.value
  const wrapperTableContainer = vTableRef.value?.$el.querySelector(
    '.v-data-table__wrapper'
  )
  const tableContainer = vTableRef.value?.$el.querySelector('table')

  const handeScroll = (event) => {
    const scrollLeftValue = event?.target.scrollLeft
    tableContainer.style.transform = `translateX(${-scrollLeftValue}px)`
    wrapperScrollbarBottom.scrollLeft = scrollLeftValue
  }

  wrapperScrollbarBottom?.addEventListener('scroll', handeScroll)

  let resizeObsBottom = new ResizeObserver((entries) => {
    for (let entry of entries) {
      const cr = entry.contentRect
      const scrollbarBottomContainer = scrollBarBottomContainerRef.value
      scrollbarBottomContainer.style.width = cr.width.toString() + 'px'
    }
  })

  if (tableContainer && wrapperTableContainer) {
    resizeObsBottom.observe(tableContainer)
    return () => {
      resizeObsBottom.unobserve(tableContainer)
    }
  }

  // Return null if no observation is set up
  return null
}
export const validateDataFeeTable = (itemFee, arItem) => {
  return itemFee
    .filter((item) => arItem.some((valItem) => valItem.id === item.ar_item_id))
    .map((item) => {
      // update name
      const validateItem = arItem.find(
        (valItem) => valItem.id === item.ar_item_id
      )
      if (validateItem) {
        return { ...item, fee: validateItem.name }
      }
      return item
    })
}

const normalizeText = (text) =>
  convertToWidthCharacter(text?.normalize('NFKC') || '', 'full')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

//Customize the filter function of v-autocomplete
export const filterBySearchString = (
  item,
  queryText,
  searchField = 'searchstr'
) => {
  const normalizeQueryText = convertToWidthCharacter(
    queryText?.normalize('NFKC'),
    'full'
  )
    .toLowerCase()
    .replace(/\s+/g, ' ')
  const convertQueryText = romajiToKana(normalizeQueryText)
  const searches = [
    ...normalizeQueryText.trim().split(/\s+/),
    ...convertQueryText.trim().split(/\s+/)
  ]
  if (item[searchField]) {
    const normalizeSearchStr = convertToWidthCharacter(
      item[searchField]?.normalize('NFKC'),
      'full'
    )
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (searches.some((word) => normalizeSearchStr.includes(word.trim())))
      return item
  }
  return undefined
}

export const filterOnComboBox = (
  items,
  queryText,
  splitters = [':'],
  reorderItem = true
) => {
  if (!queryText || queryText === '') {
    return items
  }
  let searches = [queryText]
  const normalizeQueryText = convertToWidthCharacter(
    queryText?.normalize('NFKC'),
    'full'
  )
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
  let convertSearches = []
  const convertQueryText = romajiToKana(normalizeQueryText)

  if (splitters.length) {
    // split string 'code: name(description~)' to array ["code", "name", "description~"]
    const splitterRegex = RegExp('[^' + _.join(splitters, '/') + ']+', 'g')
    searches = _.words(normalizeQueryText, splitterRegex).flatMap((w) =>
      w.trim().split(/\s+/)
    )
    convertSearches = _.words(convertQueryText, splitterRegex).flatMap((w) =>
      w.trim().split(/\s+/)
    )
    if (!searches.length) searches = normalizeQueryText.trim().split(/\s+/)
    if (!convertSearches.length)
      convertSearches = convertQueryText.trim().split(/\s+/)
  }

  const searchItemsIncludes = items.filter((item) => {
    const searchStr = item.searchstr
      ? convertToWidthCharacter(item.searchstr?.normalize('NFKC'), 'full')
          .toLowerCase()
          .replace(/\s+/g, ' ')
      : convertToWidthCharacter(
          `${item.code}: ${item.name}`.normalize('NFKC'),
          'full'
        )
          .toLowerCase()
          .replace(/\s+/g, ' ')
    return (
      searches.every((word) =>
        searchStr.normalize('NFKC').includes(word.trim().normalize('NFKC'))
      ) ||
      convertSearches.every((word) =>
        searchStr.normalize('NFKC').includes(word.trim().normalize('NFKC'))
      )
    )
  })
  // we don't sort in frontnend instead we sort in backend by priority: code > name, but if reorderItem is false, we will not prioritize code, it means the search result will be sorted by name only
  // // Sort by priority: code > name
  // searchItemsIncludes.sort((a, b) => {
  //   if (reorderItem) {
  //     const normalizedCodeA = normalizeText(String(a.code || ''))
  //     const normalizedCodeB = normalizeText(String(b.code || ''))
  //     // code split from string 'code: name(description~)' or input text
  //     const extractedQueryCode = searches[0] || normalizeQueryText

  //     // If the code exactly matches the keyword, prioritize it to the top.
  //     if (normalizedCodeA === extractedQueryCode) return -1
  //     if (normalizedCodeB === extractedQueryCode) return 1
  //   }

  //   const startsWithComparison = (valueA, valueB) =>
  //     valueB.toLowerCase().replace(/\s+/g, ' ').startsWith(normalizeQueryText) -
  //     valueA.toLowerCase().replace(/\s+/g, ' ').startsWith(normalizeQueryText)
  //   return (
  //     startsWithComparison(
  //       convertToWidthCharacter(
  //         String(a.code || '')?.normalize('NFKC'),
  //         'full'
  //       ),
  //       convertToWidthCharacter(String(b.code || '')?.normalize('NFKC'), 'full')
  //     ) ||
  //     startsWithComparison(a.code || '', b.code || '') ||
  //     startsWithComparison(a.name || '', b.name || '')
  //   )
  // })
  return [...searchItemsIncludes]
}

export const normalizeString = (str) => {
  str = str
    ? str
        .toString()
        .replaceAll(/−|ｰ|ー/gi, '-')
        .normalize('NFKD')
    : ''
  return convertToWidthCharacter(str.normalize('NFKC'), 'half').toLowerCase()
}
/**
 * Retrieves the first item from the provided list that matches the input text.
 * If no match is found, returns null.
 *
 * @param {string} text - The input text to search for.
 * @param {Array} propsList - The list of items to search within.
 * @returns {Object|null} - The first item that matches the input text, or null if no match is found.
 */
export const getDataByInputText = (text, propsList, reorderItem = true) => {
  if (!text) text = ''
  const matchedLst = filterOnComboBox(propsList, text, undefined, reorderItem)
  return matchedLst.length > 0 ? matchedLst[0] : null
}
export function safeParseInt(str) {
  if (/^\d+$/.test(str)) return parseInt(str, 10)
  return NaN
}

class ChangeTracker {
  constructor() {
    this._store = new WeakMap()
  }
  track(object) {
    // Clone the object
    const clone = _.cloneDeep(object)
    // Store it
    this._store.set(object, clone)
  }
  getOriginal(object) {
    // Access everything so if this function is used inside a
    // computed property, Vue will refresh the computed value
    // when object changes
    for (const prop in object) {
      // eslint-disable-next-line no-unused-vars
      const __ = object[prop]
    }
    // When return original, deep clone it so whoever
    // uses the result won't accidentally change the original value
    return _.cloneDeep(this._store.get(object))
  }
  getRawOriginal(object) {
    return this._store.get(object)
  }
  changedProps(object, customCompare = null) {
    /**
     * object: Object | Array
     * customCompare: Function(newValue, oldValue) -> Boolean
     */
    const clone = this.getOriginal(object)
    if (clone === undefined) {
      console.warn(
        'This object was not tracked. Call objectTracker.track(object) first.'
      )
      return undefined
    }
    const props = []
    for (const prop in object) {
      if (customCompare) {
        if (!_.isEqualWith(object[prop], clone[prop], customCompare)) {
          props.push(prop)
        }
        continue
      }
      if (!_.isEqual(object[prop], clone[prop])) {
        props.push(prop)
      }
    }
    return props
  }
  changed(object, customCompare = null) {
    /**
     * object: Object | Array
     * customCompare: Function(newValue, oldValue) -> Boolean
     */
    const objChanged = this.changedProps(object, customCompare)
    if (objChanged === undefined) return undefined
    return _.pick(object, objChanged)
  }
  /**
   * Mark a property as unchanged by copying value back to original object
   */
  setUnchanged(object, prop) {
    const original = this._store.get(object)
    original[prop] = _.cloneDeep(object[prop])
  }
}

export const changeTracker = new ChangeTracker()

export const calculateFreightAmount = (data, rowIdx) => {
  if (data[rowIdx].freight_unit_cost && data[rowIdx].freight_bundle_num) {
    data[rowIdx].freight_amount =
      parseFloat(data[rowIdx].freight_unit_cost) *
      parseFloat(data[rowIdx].freight_bundle_num)
  } else {
    data[rowIdx].freight_amount = 0
  }
  // remove #ERROR in freight_amount if have
  if (data[rowIdx].$isValidate)
    delete data[rowIdx].$isValidate['freight_amount']
}

/**
 * Calculate freight tax for rows[rowIdx]
 * freight_tax = freight_amount * tax_rate if tax_type = 外税, then round based on fraction_type
 * freight_tax = freight_amount - (freight_amount/ (1 + tax_rate)) tax_rate if tax_type = 内税, then round based on fraction_type
 * otherwise freight_tax = 0
 */
export const calculateFreightTax = (
  data,
  rowIdx,
  taxFraction,
  taxType,
  taxRate
) => {
  let taxAmount = 0
  const currentTaxType = taxType
  let freightAmount = parseFloat(data[rowIdx].freight_amount)
  let currentTaxRate = parseFloat(taxRate) / 100
  // Prevent NaN values
  if (isNaN(freightAmount)) freightAmount = 0
  if (isNaN(currentTaxRate)) currentTaxRate = 0
  // Calculate tax amount based on tax_type
  if (currentTaxType) {
    if (currentTaxType.code === TaxTypeId.TAX_EXCLUDED)
      taxAmount = freightAmount * currentTaxRate
    if (currentTaxType.code === TaxTypeId.TAX_INCLUDED)
      taxAmount = freightAmount - freightAmount / (1 + currentTaxRate)
    if (currentTaxType.code === TaxTypeId.TAX_FREE) taxAmount = 0
  }

  // Round tax amount based on tax fraction
  if (taxFraction && taxFraction.code === TaxFractionId.ROUND_CEIL)
    taxAmount = Math.ceil(taxAmount)
  else if (taxFraction && taxFraction.code === TaxFractionId.ROUND_FLOOR)
    taxAmount = Math.floor(taxAmount)
  else taxAmount = Math.round(taxAmount)
  data[rowIdx].freight_tax = taxAmount
}

/**
 * calculate total freight_amount after tax
 * total_freight_amount = freight_amount + freight_tax if tax_type is 外税
 * Otherwise, total_freight_amount = freight_amount
 */
export const calculateTotal = (data, rowIdx, taxType) => {
  const currentTaxType = taxType
  const freightAmt = parseFloat(data[rowIdx].freight_amount ?? 0)
  const taxAmount = parseFloat(data[rowIdx].freight_tax ?? 0)
  if (currentTaxType && currentTaxType.code === TaxTypeId.TAX_EXCLUDED) {
    data[rowIdx].total = freightAmt + taxAmount
  } else data[rowIdx].total = freightAmt
}

export const calFreightTax = (freightAmount, taxRate, taxType, taxFraction) => {
  let freightTax = 0
  let total = 0
  let currentTaxRate = parseFloat(taxRate) / 100
  let currentFreightAmount = parseFloat(freightAmount)
  // Prevent NaN values
  if (isNaN(currentFreightAmount)) currentFreightAmount = 0
  if (isNaN(currentTaxRate)) currentTaxRate = 0
  // Calculate tax amount based on tax_type
  if (taxType) {
    if (taxType.code === TaxTypeId.TAX_EXCLUDED)
      freightTax = currentFreightAmount * currentTaxRate
    if (taxType.code === TaxTypeId.TAX_INCLUDED)
      freightTax =
        currentFreightAmount - currentFreightAmount / (1 + currentTaxRate)
    if (taxType.code === TaxTypeId.TAX_FREE) freightTax = 0
  }

  // Round tax amount based on tax fraction
  if (taxFraction && taxFraction.code === TaxFractionId.ROUND_CEIL)
    freightTax = Math.ceil(freightTax)
  else if (taxFraction && taxFraction.code === TaxFractionId.ROUND_FLOOR)
    freightTax = Math.floor(freightTax)
  else freightTax = Math.round(freightTax)

  //calculate total
  if (taxType && taxType.code === TaxTypeId.TAX_EXCLUDED) {
    total = currentFreightAmount + freightTax
  } else total = currentFreightAmount

  return [freightTax, total]
}

export const sortOnCombobox = (items, queryText) => {
  const normalizeQueryText = convertToWidthCharacter(
    queryText?.normalize('NFKC'),
    'full'
  )
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
  const searchItemsIncludes = items.sort((a, b) => {
    const normalizedCodeA = normalizeText(String(a.code || ''))
    const normalizedCodeB = normalizeText(String(b.code || ''))

    // If the code exactly matches the keyword, prioritize it to the top.
    if (normalizedCodeA === normalizeQueryText) return -1
    if (normalizedCodeB === normalizeQueryText) return 1
    const startsWithComparison = (valueA, valueB) =>
      valueB.toLowerCase().replace(/\s+/g, ' ').startsWith(normalizeQueryText) -
      valueA.toLowerCase().replace(/\s+/g, ' ').startsWith(normalizeQueryText)
    return (
      startsWithComparison(
        convertToWidthCharacter(
          String(a.code || '')?.normalize('NFKC'),
          'full'
        ),
        convertToWidthCharacter(String(b.code || '')?.normalize('NFKC'), 'full')
      ) ||
      startsWithComparison(a.code || '', b.code || '') ||
      startsWithComparison(a.name || '', b.name || '')
    )
  })
  return searchItemsIncludes
}

export const setupCustomScroll = (
  wrapperScrollbarId,
  scrollbarContainerId,
  pulledTargetId
) => {
  const wrapperScrollbarBot = document.getElementById(
    'wrapper-scrollbar-' + wrapperScrollbarId
  )
  const wrapperTableContainer = document.getElementById(pulledTargetId)
  const tableContainer = wrapperTableContainer.querySelector('table')
  const freezeElements = tableContainer.querySelectorAll('.freeze')
  function syncScroll(event) {
    const scrollLeftValue = event?.target.scrollLeft
    tableContainer.style.transform = `translateX(${-scrollLeftValue}px)`

    if (freezeElements) {
      freezeElements.forEach((element) => {
        element.style.transform = `translateX(${scrollLeftValue}px)`
      })
    }

    if (event.target === wrapperScrollbarBot) {
      if (wrapperScrollbarBot) {
        wrapperScrollbarBot.scrollLeft = scrollLeftValue
      }
    }
  }
  // //Take all element change attributes and style in mutationsList
  function updateScrollWhenTableContainerMove(mutationsList, observer) {
    mutationsList.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'style'
      ) {
        const element = mutation.target
        const style = window.getComputedStyle(element)
        const translateXValue =
          style.transform && style.transform !== 'none'
            ? style.transform.split('(')[1].split(')')[0].split(',')[4]
            : '0'
        if (wrapperScrollbarBot)
          wrapperScrollbarBot.scrollLeft = -translateXValue
      }
    })
  }
  const observer = new MutationObserver(updateScrollWhenTableContainerMove)
  const config = { attributes: true, attributeFilter: ['style'] }
  // Start observe tableContainer change
  observer.observe(tableContainer, config)
  wrapperScrollbarBot?.addEventListener('scroll', syncScroll)

  let resizeObs = new ResizeObserver((entries) => {
    for (let entry of entries) {
      const cr = entry.contentRect
      const scrollbarBotContainer = document.getElementById(
        'scrollbar-container-' + scrollbarContainerId
      )
      // padding left 4px, padding right 4px, need to plus 8px
      if (scrollbarBotContainer)
        scrollbarBotContainer.style.width = cr.width.toString() + 'px'
      // Fix bug on Windows: Set height != 0 for scrollbar bot
      // to make sure browser can detect it and handle scroll for it.
      scrollbarBotContainer.style.height = '1px'
    }
  })
  if (tableContainer && wrapperTableContainer) {
    resizeObs.observe(tableContainer)
  }
  if (wrapperTableContainer && wrapperScrollbarBot) {
    wrapperScrollbarBot.onscroll = function () {
      wrapperTableContainer.scrollLeft = wrapperScrollbarBot.scrollLeft
    }
    wrapperTableContainer.onscroll = function () {
      wrapperScrollbarBot.scrollLeft = wrapperTableContainer.scrollLeft
    }
  }
  function removeListener() {
    resizeObs.unobserve(tableContainer)
    wrapperScrollbarBot?.removeEventListener('scroll', syncScroll)
  }
  return removeListener
}
export const isNotEmpty = (value) => {
  return (
    value != null && value !== '' && (!Array.isArray(value) || value.length > 0)
  )
}

export const concatCodeName = (item) => {
  if (!item) return null
  const _name = item?.name ? item.name : ''
  // return empty string if no name found
  if (!_name) return ''
  if (!item.code) return _name
  if (item?.branch_code)
    return item.code + '-' + item.branch_code + ': ' + _name
  return item.code + ': ' + _name
}
export const concatShipperName = (shipper) => {
  if (!shipper) return null
  return concatShipperCode(shipper) + (': ' + shipper.name)
}

export const concatShipperCode = (shipper) => {
  if (!shipper) return null
  const codes = [shipper.prefecture_code, shipper.organization_type_code]
  // ex: 01-5
  if (!shipper.agri_group_code && !shipper.personal_code) return codes.join('-')
  // ex: 01-5-1001
  if (!shipper.personal_code)
    return [...codes, shipper.agri_group_code].join('-')
  // ex: 01-5--10
  if (!shipper.agri_group_code)
    return [...codes, '', shipper.personal_code].join('-')
  // ex: 01-5-1001-10
  return [...codes, shipper.agri_group_code, shipper.personal_code].join('-')
}

export const closeAllPopup = (refs) => {
  Object.values(refs).forEach((ref) => {
    if (!ref?.$el) return
    if (ref.$el.classList.contains('v-autocomplete')) ref.blur()
    if (
      ref.$el.classList.contains('chip-combobox') ||
      ref.$el.classList.contains('shipper-input')
    )
      ref.closePopup()
  })
}

export const onEnterAutocomplete = ($nextTick, $refs, e) => {
  $nextTick(() => {
    $refs[`${e}`].isMenuActive = false
  })
  setTimeout(() => {
    $refs[`${e}`]?.blur()
  }, 200)
}

export const customFilterDropdown = (item, queryText, itemText) => {
  const searchTerm = convertToWidthCharacter(
    queryText ? queryText.trim().toLowerCase().normalize('NFKC') : '',
    'half'
  )
  let normalizeItemName = ''
  if (itemText && item[itemText] && typeof item[itemText] === 'string') {
    normalizeItemName = convertToWidthCharacter(
      item[itemText]
        ? item[itemText].toString().toLowerCase().normalize('NFKC')
        : '',
      'half'
    )
  } else if (typeof item === 'string') {
    normalizeItemName = convertToWidthCharacter(
      item ? item.toString().toLowerCase().normalize('NFKC') : '',
      'half'
    )
  }
  return normalizeItemName.includes(searchTerm)
}

//  Functions for string conversion copy from backend from python to javascript
//  文字列変換用の関数群です
export const convert = (str, option) => {
  if (!option) {
    return str
  }

  if (option.includes('r')) {
    // Convert full-width letters to half-width letters
    str = str.replace(/[Ａ-Ｚａ-ｚ]/g, (match) =>
      String.fromCharCode(match.charCodeAt(0) - 65248)
    )
  }

  if (option.includes('R')) {
    // Convert half-width letters to full-width letters
    str = str.replace(/[A-Za-z]/g, (match) =>
      String.fromCharCode(match.charCodeAt(0) + 65248)
    )
  }

  if (option.includes('n')) {
    // Convert full-width numbers to half-width numbers
    str = str.replace(/[０-９]/g, (match) =>
      String.fromCharCode(match.charCodeAt(0) - 65248)
    )
  }

  if (option.includes('N')) {
    // Convert half-width numbers to full-width numbers
    str = str.replace(/[0-9]/g, (match) =>
      String.fromCharCode(match.charCodeAt(0) + 65248)
    )
  }

  if (option.includes('a')) {
    // Convert full-width alphanumeric characters and symbols to half-width
    str = str.replace(
      /[Ａ-Ｚａ-ｚ０-９！＃＄％＆（）＊＋，－．／：；＜＝＞？＠［］＾＿｀｛｜｝]/g,
      (match) => String.fromCharCode(match.charCodeAt(0) - 65248)
    )
  }

  if (option.includes('A')) {
    // Convert half-width alphanumeric characters and symbols to full-width
    str = str.replace(/[A-Za-z0-9!#$%&()*+,\-./:;<=>?@[\]^_`{|}]/g, (match) =>
      String.fromCharCode(match.charCodeAt(0) + 65248)
    )
  }

  if (option.includes('s')) {
    // Convert full-width space to half-width space (U+3000 -> U+0020)
    str = str.replace(/\u3000/g, '\u0020')
  }

  if (option.includes('S')) {
    // Convert half-width space to full-width space (U+0020 -> U+3000)
    str = str.replace(/\u0020/g, '\u3000')
  }

  if (option.includes('c')) {
    // Convert full-width Katakana to full-width Hiragana
    str = str
      .replace(/[ァ-ン]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 0x60)
      )
      .replace('ヴ', 'う゛')
      .replace('ヵ', 'か')
      .replace('ヽ', 'ゝ')
      .replace('ヾ', 'ゞ')
  }

  if (option.includes('C')) {
    // Convert full-width Hiragana to full-width Katakana
    str = str
      .replace(/[ぁ-ん]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) + 0x60)
      )
      .replace('ゝ', 'ヽ')
      .replace('ゞ', 'ヾ')
  }

  if (option.includes('v')) {
    // Convert "う濁" to "は濁"
    str = str
      .replace(/(う゛ぁ|ゔぁ)/g, 'ば')
      .replace(/(う゛ぃ|ゔぃ)/g, 'び')
      .replace(/(う゛ぇ|ゔぇ)/g, 'べ')
      .replace(/(う゛ぉ|ゔぉ)/g, 'ぼ')
      .replace(/(う゛|ゔ)/g, 'ぶ')
  }

  if (option.includes('V')) {
    // Convert "ウ濁" to "ハ濁"
    str = str
      .replace(/(ウ゛ァ|ヴァ)/g, 'バ')
      .replace(/(ウ゛ィ|ヴィ)/g, 'ビ')
      .replace(/(ウ゛ェ|ヴェ)/g, 'ベ')
      .replace(/(ウ゛ォ|ヴォ)/g, 'ボ')
      .replace(/(ウ゛|ヴ)/g, 'ブ')
  }

  if (option.includes('Q')) {
    // Convert half-width quotes and apostrophes to full-width
    str = str.replace(/"/g, '＂').replace(/'/g, '＇')
  }

  if (option.includes('q')) {
    // Convert full-width quotes and apostrophes to half-width
    str = str.replace(/＂/g, '"').replace(/＇/g, "'")
  }

  if (option.includes('B')) {
    // Convert half-width backslash to full-width
    str = str.replace(/\\/g, '＼')
  }

  if (option.includes('b')) {
    // Convert full-width backslash to half-width
    str = str.replace(/＼/g, '\\')
  }

  if (option.includes('T')) {
    // Convert half-width tilde to full-width tilde
    str = str.replace(/~/g, '〜')
  }

  if (option.includes('t')) {
    // Convert full-width tilde to half-width tilde
    str = str.replace(/～/g, '~')
  }

  if (option.includes('W')) {
    // Convert full-width wave dash to full-width tilde
    str = str.replace(/〜/g, '～')
  }

  if (option.includes('w')) {
    // Convert full-width tilde to full-width wave dash
    str = str.replace(/～/g, '〜')
  }

  if (option.includes('P')) {
    // Convert half-width dashes to full-width dashes
    str = str.replace(/ー/g, '－').replace(/ｰ/g, '－')
  }

  if (option.includes('p')) {
    // Convert full-width dashes to half-width dashes
    str = str.replace(/－/g, '-')
  }

  if (option.includes('U')) {
    // Convert half-width symbols to full-width symbols
    const hanMap = {
      '⦅': '｟',
      '⦆': '｠',
      '¢': '￠',
      '£': '￡',
      '¬': '￢',
      '¯': '￣',
      '¦': '￤',
      '¥': '￥',
      '₩': '￦',
      '￨': '│',
      '￩': '←',
      '￪': '↑',
      '￫': '→',
      '￬': '↓',
      '￭': '■',
      '￮': '○'
    }
    Object.keys(hanMap).forEach((char) => {
      str = str.replace(new RegExp(char, 'g'), hanMap[char])
    })
  }

  if (option.includes('u')) {
    // Convert full-width symbols to half-width symbols
    const zenMap = {
      '｟': '⦅',
      '｠': '⦆',
      '￠': '¢',
      '￡': '£',
      '￢': '¬',
      '￣': '¯',
      '￤': '¦',
      '￥': '¥',
      '￦': '₩',
      '│': '￨',
      '←': '￩',
      '↑': '￪',
      '→': '￫',
      '↓': '￬',
      '■': '￭',
      '○': '￮'
    }
    Object.keys(zenMap).forEach((char) => {
      str = str.replace(new RegExp(char, 'g'), zenMap[char])
    })
  }

  if (option.includes('X')) {
    // Convert specific characters
    const kakomiMap = { '⑴': '(1)' }
    Object.keys(kakomiMap).forEach((char) => {
      str = str.replace(new RegExp(char, 'g'), kakomiMap[char])
    })
  }

  if (option.includes('Y')) {
    // Convert specific characters
    const syuugouMap = { '㌀': 'アパート' }
    Object.keys(syuugouMap).forEach((char) => {
      str = str.replace(new RegExp(char, 'g'), syuugouMap[char])
    })
  }

  if (option.includes('Z')) {
    // Convert small kana to '@'
    const komojiMap = { '\ufe6b': '\u0040' }
    Object.keys(komojiMap).forEach((char) => {
      str = str.replace(new RegExp(char, 'g'), komojiMap[char])
    })
  }

  // Trim and remove extra spaces except newlines
  str = str.trim().replace(/[^\S\n]+/g, ' ')

  return str
}

export const downloadFile = async (data, type, name) => {
  const url = URL.createObjectURL(new Blob([data]))
  const link = document.createElement('a')
  link.href = url
  link.download = `${name}.${type}`
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export const returnValueOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null
  return value
}

export const updateUrlQuery = (oldRouter, params) => {
  // update url query params
  const query = { ...oldRouter }
  for (const key in params) {
    if (
      !params[key] ||
      (Array.isArray(params[key]) && params[key].length === 0)
    )
      delete query[key]
    else query[key] = params[key].toString()
  }
  replaceUrl({ query })
}
export const calcTotal = (items, key) => {
  let total = 0
  items.forEach((item) => {
    total += item[key] || 0
  })
  return total
}
export const yenWrapper = (value) => {
  const displayValue = numberToStringDash(value)
  if (displayValue === '-') return displayValue
  return i18n.t('common.yen') + displayValue
}
export const numberToStringDash = (value) => {
  if (value === null) return '-'
  else return numberToString(value, false, 2, true)
}

export const dateWrapper = (date, format) => {
  if (!date) return null
  const dateMoment = moment(date)
  return dateMoment._isValid ? dateMoment.format(format) : ''
}

export const convertDateStringToYearMonth = (dateString) => {
  if (!dateString) return null
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0') // Add leading zero if needed 1
  return `${year}-${month}`
}

/*
  Code resolves closing date ranges based on input codes:
  - "00": first day of month
  - "ST": week or Saturday cycles
  - "99": last day of month
  - numeric: specific day of month
*/
export const getFromToByClosingDate = (input, todayString) => {
  // cache key moments
  const now = moment(todayString).startOf('day')
  const monthStart = now.clone().startOf('month')
  const monthEnd = now.clone().endOf('month')
  const nextMonthStart = monthStart.clone().add(1, 'month')
  const prevMonthStart = monthStart.clone().subtract(1, 'month')

  // helper: first Saturday on or after a given date
  const firstSaturday = (date) => {
    const sat = date.clone().day(6)
    return sat.isBefore(date, 'day') ? sat.add(7, 'days') : sat
  }
  const firstSatPrev = firstSaturday(prevMonthStart)
  const firstSatThis = firstSaturday(monthStart)
  const firstSatNext = firstSaturday(nextMonthStart)

  // collect valid codes
  const values = [
    input.date_1,
    input.date_2,
    input.date_3,
    input.date_4,
    input.date_5
  ].filter(Boolean)

  // multi-ST: full week Sunday->Saturday
  if (values.length > 1 && values.every((v) => v === 'ST')) {
    const start = now.clone().day(0)
    return {
      from_date: start.format('YYYY-MM-DD'),
      to_date: start.clone().add(6, 'days').format('YYYY-MM-DD')
    }
  }

  // single-ST: cycle between consecutive first Saturdays
  if (values.length === 1 && values[0] === 'ST') {
    const endSat = now.isSameOrBefore(firstSatThis, 'day')
      ? firstSatThis
      : firstSatNext
    const startSat = endSat.isSame(firstSatThis, 'day')
      ? firstSatPrev
      : firstSatThis
    return {
      from_date: startSat.clone().add(1, 'day').format('YYYY-MM-DD'),
      to_date: endSat.format('YYYY-MM-DD')
    }
  }

  // single-00: cycle on 1st
  if (values.length === 1 && values[0] === '00') {
    if (now.isSame(monthStart, 'day')) {
      const prevStart = monthStart.clone().subtract(1, 'month')
      return {
        from_date: prevStart.clone().add(1, 'day').format('YYYY-MM-DD'),
        to_date: monthStart.format('YYYY-MM-DD')
      }
    }
    return {
      from_date: monthStart.clone().add(1, 'day').format('YYYY-MM-DD'),
      to_date: nextMonthStart.format('YYYY-MM-DD')
    }
  }

  // numeric-cycle: if today <= first numeric code, return [1st -> that day]
  const numericDays = values
    .filter((v) => !['ST', '00', '99'].includes(v))
    .map((v) => parseInt(v, 10))
    .filter((n) => !isNaN(n))
    .sort((a, b) => a - b)
  if (numericDays.length) {
    const cutoff = monthStart.clone().date(numericDays[0])
    if (now.isSameOrBefore(cutoff, 'day')) {
      return {
        from_date: monthStart.format('YYYY-MM-DD'),
        to_date: cutoff.format('YYYY-MM-DD')
      }
    }
  }

  // fallback: build all specified dates
  const allDates = values.flatMap((code) => {
    switch (code) {
      case '00':
        return [monthStart.clone()]
      case '99':
        return [monthEnd.clone()]
      default:
        return [monthStart.clone().date(Number(code))]
    }
  })

  // dedupe & sort
  const unique = Array.from(
    new Set(allDates.map((d) => d.format('YYYY-MM-DD')))
  )
    .map((s) => moment(s))
    .sort((a, b) => a.diff(b))

  // determine to_date and from_date
  const toMoment =
    unique.find((d) => d.isSameOrAfter(now, 'day')) || unique[unique.length - 1]
  const prev = unique.filter((d) => d.isBefore(toMoment, 'day'))
  const fromMoment = prev.length ? prev.pop().add(1, 'day') : monthStart

  return {
    from_date: fromMoment.format('YYYY-MM-DD'),
    to_date: toMoment.format('YYYY-MM-DD')
  }
}

export const getIdByCode = (code, items) => {
  const matchItem = items.find((item) => item.code === code)
  if (!matchItem) return null
  return matchItem.id
}
export const getIdByCodeName = (codeName, items) => {
  const matchItem = items.find((item) => concatCodeName(item) === codeName)
  if (!matchItem) return null
  return matchItem.id
}

// 品種ドロップダウンの表示用。切鉢区分（花区分）名称の先頭1文字を [切] / [鉢] の形で返す。
export const flowerTypePrefix = (variety) => {
  const name = variety?.flower_type_name
  return typeof name === 'string' && name.length > 0
    ? `[${name.charAt(0)}]`
    : ''
}

export const getWorkingDayFrom = async (date) => {
  date = moment(date).format('YYYY-MM-DD')
  const { data } = await api.get(
    `/bank_calendar/first_working_day?date_from=${date}`
  )
  return data ? data.business_date : null
}

export const getMondayOfAfterNextWeek = async (jobDate) => {
  const daysUntilMonday = (7 - new Date(jobDate).getDay() + 1) % 7 // Calculate days until next Monday
  const afterNextWeekMonday = new Date()
  afterNextWeekMonday.setDate(new Date(jobDate).getDate() + daysUntilMonday + 7)
  const workingDay = await getWorkingDayFrom(afterNextWeekMonday)
  return workingDay
}

export const roundByTaxFractionType = (number, taxTypeName) => {
  let result = 0

  // Constants for rounding types
  const TAX_FRACTION_CEIL = '切り上げ'
  const TAX_FRACTION_FLOOR = '切り捨て'

  if (taxTypeName === TAX_FRACTION_CEIL) {
    result = Math.ceil(number)
  } else if (taxTypeName === TAX_FRACTION_FLOOR) {
    result = Math.floor(number)
  } else {
    result = Math.round(number)
  }

  return result
}

const convertToFullWidth = (text) => {
  if (!text) return null
  return convertToWidthCharacter(text.normalize('NFKC'), 'full')
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

export const selectMatchBuyer = (_searchStr, buyerList) => {
  if (buyerList.length <= 0 || !_searchStr) return null

  // In case of receiving _searchStr from Edit Table it has the form "1001-00: 1001_00_buyer"
  // -> just get the value "1001-00"
  _searchStr = _searchStr?.split(':')[0]

  const searchText = convertToFullWidth(_searchStr?.trim())
  /**
   * Check if the code the buyer sent is valid or not
   * - 1001      -> true
   * - 1001-00   -> true
   * - 1001a     -> false
   * - -1001     -> false
   * - abcd      -> false
   */
  const is_search_code = /^[\d０-９]+(-[\d０-９]+)?$/.test(searchText)
  if (is_search_code) {
    // Search buyer by code
    const parts = searchText.split('-')
    const code = parts[0]
    const branchCode = parts.length > 1 ? parts[1] : null

    const _buyer = buyerList.find((buyerObj) => {
      // In some cases, the pre-generated code is {{ code-branch_code }}
      // -> just get the value {{ code }}
      const _buyer_code = buyerObj?.code ? buyerObj.code.split('-')[0] : null
      const _buyer_branch_code = buyerObj?.branch_code

      return (
        convertToFullWidth(_buyer_code) === code &&
        convertToFullWidth(_buyer_branch_code) === branchCode
      )
    })
    return _buyer ? _buyer : null
  }

  return null
}

export function useBeforePageLeave(isBlocked, message, callback) {
  const handleBeforeUnload = (event) => {
    if (isBlocked.value) {
      event.preventDefault()
      event.returnValue = message
      if (callback && typeof callback === 'function') {
        callback(event)
      }
    }
  }

  onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
  })
}

export const hardReload = () => {
  const currentUrl = window.location.href
  const timestamp = new Date().getTime() // Generate a unique timestamp

  // Append the timestamp as a query parameter
  const newUrl = currentUrl.includes('?')
    ? `${currentUrl}&t=${timestamp}`
    : `${currentUrl}?t=${timestamp}`

  window.location.href = newUrl
}

export const sortByOdrnumAndCode = (a, b) => {
  const hasOrderA = a.odrnum != null
  const hasOrderB = b.odrnum != null

  if (hasOrderA && hasOrderB) {
    return a.odrnum - b.odrnum
  }

  if (hasOrderA) return -1
  if (hasOrderB) return 1

  const splitA = (a.code || '').split('-')
  const splitB = (b.code || '').split('-')

  const len = Math.max(splitA.length, splitB.length)
  for (let i = 0; i < len; i++) {
    const partA = splitA[i] || ''
    const partB = splitB[i] || ''

    const numA = parseInt(partA, 10)
    const numB = parseInt(partB, 10)

    const bothNumeric = !isNaN(numA) && !isNaN(numB)

    if (bothNumeric) {
      if (numA !== numB) return numA - numB
    } else {
      const cmp = partA.localeCompare(partB)
      if (cmp !== 0) return cmp
    }
  }

  // need to compare branch_code if it have
  const branchA = a.branch_code || ''
  const branchB = b.branch_code || ''
  const branchCmp = branchA.localeCompare(branchB)
  if (branchCmp !== 0) return branchCmp

  return 0
}

export const filterOnComboBox4Shipper = (
  items,
  queryText,
  splitters = [':'],
  reorderItem = true
) => {
  if (!queryText || queryText === '') {
    return items
  }
  let searches = [queryText]
  const normalizeQueryText = convertToWidthCharacter(
    queryText?.normalize('NFKC'),
    'full'
  )
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
  let convertSearches = []
  const convertQueryText = romajiToKana(normalizeQueryText)

  if (splitters.length) {
    // split string 'code: name(description~)' to array ["code", "name", "description~"]
    const splitterRegex = RegExp('[^' + _.join(splitters, '/') + ']+', 'g')
    searches = _.words(normalizeQueryText, splitterRegex).flatMap((w) =>
      w.trim().split(/\s+/)
    )
    convertSearches = _.words(convertQueryText, splitterRegex).flatMap((w) =>
      w.trim().split(/\s+/)
    )
    if (!searches.length) searches = normalizeQueryText.trim().split(/\s+/)
    if (!convertSearches.length)
      convertSearches = convertQueryText.trim().split(/\s+/)
  }

  const searchItemsIncludes = items.filter((item) => {
    const searchStr = item.searchstr
      ? convertToWidthCharacter(item.searchstr?.normalize('NFKC'), 'full')
          .toLowerCase()
          .replace(/\s+/g, ' ')
      : convertToWidthCharacter(
          `${item.code}: ${item.name}`.normalize('NFKC'),
          'full'
        )
          .toLowerCase()
          .replace(/\s+/g, ' ')

    return (
      searches.some((word) =>
        word
          .trim()
          .split(/\s+/)
          .every((sub) =>
            searchStr.normalize('NFKC').includes(sub.normalize('NFKC'))
          )
      ) ||
      convertSearches.some((word) =>
        word
          .trim()
          .split(/\s+/)
          .every((sub) =>
            searchStr.normalize('NFKC').includes(sub.normalize('NFKC'))
          )
      )
    )
  })

  // Sort by priority: code > name
  // searchItemsIncludes.sort((a, b) => {
  //   if (reorderItem) {
  //     const normalizedCodeA = normalizeText(String(a.code || ''))
  //     const normalizedCodeB = normalizeText(String(b.code || ''))
  //     // code split from string 'code: name(description~)' or input text
  //     const extractedQueryCode = searches[0] || normalizeQueryText

  //     // If the code exactly matches the keyword, prioritize it to the top.
  //     if (normalizedCodeA === extractedQueryCode) return -1
  //     if (normalizedCodeB === extractedQueryCode) return 1
  //   }

  //   const startsWithComparison = (valueA, valueB) =>
  //     valueB.toLowerCase().replace(/\s+/g, ' ').startsWith(normalizeQueryText) -
  //     valueA.toLowerCase().replace(/\s+/g, ' ').startsWith(normalizeQueryText)
  //   return (
  //     startsWithComparison(
  //       convertToWidthCharacter(
  //         String(a.code || '')?.normalize('NFKC'),
  //         'full'
  //       ),
  //       convertToWidthCharacter(String(b.code || '')?.normalize('NFKC'), 'full')
  //     ) ||
  //     startsWithComparison(a.code || '', b.code || '') ||
  //     startsWithComparison(a.name || '', b.name || '')
  //   )
  // })
  return [...searchItemsIncludes]
}

export const downloadFromUrl = async (url, filename) => {
  try {
    let downloadUrl = url

    // Extract filename from URL if not explicitly provided
    const resolvedFilename =
      filename ||
      decodeURIComponent(url.split('/').pop()?.split('?')[0] || 'download')

    if (!url.startsWith('blob:')) {
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      downloadUrl = URL.createObjectURL(blob)
    }

    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = resolvedFilename
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)

    if (!url.startsWith('blob:')) {
      URL.revokeObjectURL(downloadUrl)
    }
  } catch (error) {
    console.error('Failed to download file:', error)
  }
}

export const filterItem = (item, searchString) => {
  const normalizeString = convertToWidthCharacter(
    searchString ? searchString.normalize('NFKC') : '',
    'half'
  )
  const normalizeItemName = convertToWidthCharacter(
    item.displayName ? item.displayName.normalize('NFKC') : '',
    'half'
  )
  return normalizeItemName.includes(normalizeString)
}
